/**
 * Support Module Types
 */

// Enums
export type TicketStatus = 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TicketCategory = 'general' | 'technical' | 'billing' | 'feature_request' | 'bug_report' | 'account' | 'other';

// Attachment
export interface TicketAttachment {
  id: string;
  filename: string;
  file_url: string;
  file_type?: string;
  file_size?: number;
  created_at: string;
}

// Ticket Message
export interface TicketMessage {
  id: string;
  ticket_id: string;
  user_id: string;
  message: string;
  is_from_admin: boolean;
  is_internal_note: boolean;
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
  attachments?: TicketAttachment[];
}

export interface TicketMessageCreate {
  message: string;
  is_internal_note?: boolean;
  attachments?: File[];
}

// Support Ticket
export interface SupportTicket {
  id: string;
  ticket_number: string;
  user_id: string;
  account_id?: string;
  assigned_to_id?: string;
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  is_read_by_admin: boolean;
  is_read_by_user: boolean;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  closed_at?: string;
  user_name?: string;
  user_email?: string;
  assigned_to_name?: string;
  message_count: number;
  last_message_at?: string;
}

export interface SupportTicketDetail extends SupportTicket {
  messages: TicketMessage[];
}

export interface SupportTicketCreate {
  subject: string;
  description: string;
  category?: TicketCategory;
  priority?: TicketPriority;
  attachments?: File[];
}

export interface SupportTicketListResponse {
  tickets: SupportTicket[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Translations
export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Aberto',
  in_progress: 'Em Andamento',
  waiting_customer: 'Aguardando Resposta',
  resolved: 'Resolvido',
  closed: 'Fechado',
};

export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
};

export const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  general: 'Geral',
  technical: 'Técnico',
  billing: 'Financeiro',
  feature_request: 'Sugestão',
  bug_report: 'Reportar Bug',
  account: 'Conta',
  other: 'Outros',
};

export const TICKET_STATUS_COLORS: Record<TicketStatus, string> = {
  open: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  in_progress: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  waiting_customer: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  resolved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  closed: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

export const TICKET_PRIORITY_COLORS: Record<TicketPriority, string> = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

